/**
 * Pure decision helpers extracted from GameScreen (apps/mobile/app/(app)/games/[id]/index.tsx).
 *
 * PURPOSE: Characterization-test safety net for the upcoming GameScreen split.
 * Each function lifts a branch decision or derived value out of the ~2,040-line
 * god-component and makes it independently testable.  GameScreen imports and
 * delegates to these — behavior is IDENTICAL to the inline code they replace.
 *
 * Rules:
 *   - Zero React / React-Native imports (pure TS, node-testable).
 *   - No side effects.
 *   - Input types come from @warcarp/api-types or are defined inline here.
 *   - Every function has a JSDoc comment that QUOTES the inline condition it
 *     mirrors, so a reviewer can verify the extract is faithful.
 */

import type { GameResponse, GamePlayerResponse } from '@warcarp/api-types'
import { classifyGameLoadError } from './gameLoadError'
import { takeoverNoticeForViewer } from './aiTakeoverNotice'

// ─── Shared minimal projections ───────────────────────────────────────────────

/**
 * The minimal subset of HydratedGameState that these helpers need.
 * HydratedGameState is built in useGameWithMap and fuses GameState +
 * MapDef.  Only the fields GameScreen actually branches on are included
 * here — this keeps the pure module free of the full map-geometry types.
 */
export interface GameStateProjection {
  /** Engine player list — slot ids as strings, in turn order. */
  players: string[]
  /** Index into `players` of the current player. */
  turnIndex: number
  /** Current game phase. */
  phase:
    | 'claim'
    | 'placement'
    | 'post-elim-placement'
    | 'attack'
    | 'fortify'
    | string // forward-compat
  /** Engine-eliminated player ids (slot id strings). */
  eliminated: string[]
}

// ─── 1. Top-level view selection ─────────────────────────────────────────────

/**
 * The top-level branch GameScreen renders.
 *
 * Resolution order matches the early-return + ternary chain in the component:
 *   isLoading                                         → 'loading'
 *   !game && error is 403                             → 'error-forbidden'
 *   !game && error is connection/5xx                  → 'error-connection'
 *   !game (no error or 404)                           → 'error-notfound'
 *   game.status === 'ACTIVE' && gameState && myPlayerId → 'active-player-turn'
 *   game.status === 'COMPLETE' && gameState             → 'complete'
 *   game.status !== 'LOBBY' (spinner guard)            → 'active-loading-spinner'
 *   else                                              → 'lobby'
 *
 * NOTE: 'active-player-turn' covers BOTH "my turn" and "spectating" — the
 * split between those is a SECONDARY decision handled by selectActivePhaseUI.
 */
export type TopLevelView =
  | 'loading'
  | 'time-expired'        // an AI took over the viewer's seat (live push or 403 AI_TAKEOVER)
  | 'error-forbidden'
  | 'error-connection'
  | 'error-notfound'
  | 'active-player-turn'  // game ACTIVE + gameState + myPlayerId all present
  | 'complete'            // game COMPLETE + gameState present
  | 'active-loading-spinner' // ACTIVE/COMPLETE but gameState or myPlayerId not yet ready
  | 'lobby'

export interface TopLevelViewInput {
  isLoading: boolean
  /** The game data (null when still loading or when the query errored). */
  game: GameResponse | null | undefined
  /** The hydrated game state (null until the game starts). */
  gameState: GameStateProjection | null | undefined
  /** The viewer's engine player-id (slot as string), null if not a player. */
  myPlayerId: string | null
  /** The React Query error from useGameWithMap. */
  error: unknown
  /** The signed-in user's id, to detect a seat an AI took over from them. */
  viewerUserId?: string | null
}

/**
 * Returns the top-level view branch GameScreen should render.
 *
 * Mirrors the exact early-return + ternary chain starting at line ~443 of
 * GameScreen.  No RN/React imports — pure decision function.
 */
export function selectTopLevelView(input: TopLevelViewInput): TopLevelView {
  const { isLoading, game, gameState, myPlayerId, error } = input

  if (isLoading) return 'loading'

  if (takeoverNoticeForViewer(game, input.viewerUserId)) return 'time-expired'

  if (!game) {
    const kind = classifyGameLoadError(error)
    if (kind === 'ai-takeover') return 'time-expired'
    if (kind === 'forbidden') return 'error-forbidden'
    if (kind === 'connection') return 'error-connection'
    return 'error-notfound'
  }

  // Game is present — branch on status + readiness of derived state.
  // Mirrors: `game.status === 'ACTIVE' && gameState && myPlayerId ? …`
  if (game.status === 'ACTIVE' && gameState && myPlayerId) {
    return 'active-player-turn'
  }

  // Mirrors: `game.status === 'COMPLETE' && gameState ? …`
  if (game.status === 'COMPLETE' && gameState) {
    return 'complete'
  }

  // Mirrors: `game.status !== 'LOBBY' ? … (spinner) : …`
  // This fires when ACTIVE/COMPLETE but gameState or myPlayerId isn't ready yet.
  if (game.status !== 'LOBBY') {
    return 'active-loading-spinner'
  }

  return 'lobby'
}

// ─── 2. Active-phase UI selection ────────────────────────────────────────────

/**
 * Within the 'active-player-turn' branch, which sub-view renders.
 *
 *   regionDetailsMode → 'region-details'   (overlay over the phase UI)
 *   isMyTurn:
 *     phase === 'claim'               → 'claim'
 *     phase === 'placement'           → 'placement'
 *     phase === 'post-elim-placement' → 'post-elim-placement'
 *     phase === 'attack'              → 'attack'
 *     phase === 'fortify'             → 'fortify'
 *     other                          → 'unknown-phase'
 *   !isMyTurn → 'spectator'
 *
 * This mirrors the ternary block at line ~1086 in GameScreen:
 *   `regionDetailsMode ? <RegionDetailsPanel> : isMyTurn ? <phase UIs> : <spectator>`
 */
export type ActivePhaseView =
  | 'region-details'
  | 'claim'
  | 'placement'
  | 'post-elim-placement'
  | 'attack'
  | 'fortify'
  | 'unknown-phase'
  | 'spectator'

export function selectActivePhaseView(input: {
  regionDetailsMode: boolean
  isMyTurn: boolean
  phase: string
}): ActivePhaseView {
  const { regionDetailsMode, isMyTurn, phase } = input

  if (regionDetailsMode) return 'region-details'

  if (isMyTurn) {
    if (phase === 'claim') return 'claim'
    if (phase === 'placement') return 'placement'
    if (phase === 'post-elim-placement') return 'post-elim-placement'
    if (phase === 'attack') return 'attack'
    if (phase === 'fortify') return 'fortify'
    return 'unknown-phase'
  }

  return 'spectator'
}

// ─── 3. isMyTurn derivation ──────────────────────────────────────────────────

/**
 * True iff it is the viewing player's turn.
 *
 * Mirrors lines ~580-584 of GameScreen:
 *   `game.status === 'ACTIVE' && myPlayer != null && currentTurnPlayerId != null
 *    && String(myPlayer.slot) === currentTurnPlayerId`
 *
 * The engine's turnIndex is the authoritative source — NOT game.currentPlayerIndex
 * which can drift when players are eliminated.  (See the comment block at line 572.)
 */
export function deriveIsMyTurn(input: {
  gameStatus: string
  myPlayerSlot: number | null | undefined
  gameState: GameStateProjection | null | undefined
}): boolean {
  const { gameStatus, myPlayerSlot, gameState } = input

  if (gameStatus !== 'ACTIVE') return false
  if (myPlayerSlot == null) return false
  if (!gameState) return false

  const currentTurnPlayerId = gameState.players[gameState.turnIndex] ?? null
  if (currentTurnPlayerId === null) return false

  return String(myPlayerSlot) === currentTurnPlayerId
}

// ─── 4. Winner derivation (GameScreen's inline logic) ────────────────────────

/**
 * Derives the winner player from the immutable completion snapshot when it is
 * available, then falls back to the hydrated terminal state.
 *
 * This is DISTINCT from lib/winner.ts (which reads from game.state.events).
 * GameScreen's inline logic (lines ~601-608) reads from the hydrated
 * gameState.players / gameState.eliminated to find the last surviving player.
 *
 *   "The winner is the last non-eliminated player."
 *
 * Returns null for non-COMPLETE games or when no single winner can be determined.
 * In a team game this is the winning team's first member; the whole team
 * comes from `deriveWinningTeam` (lib/winner.ts).
 */
export function deriveWinnerFromHydratedState(input: {
  gameStatus: string
  gamePlayers: GamePlayerResponse[]
  gameState: GameStateProjection | null | undefined
  winnerSlot?: number | null
  /** Team games: every winning seat. The first stands in when `winnerSlot` is missing. */
  winnerSlots?: readonly number[] | null
}): GamePlayerResponse | null {
  const { gameStatus, gamePlayers, gameState, winnerSlot, winnerSlots } = input

  if (gameStatus !== 'COMPLETE') return null
  if (winnerSlot !== null && winnerSlot !== undefined) {
    return gamePlayers.find((p) => p.slot === winnerSlot) ?? null
  }
  if (winnerSlots && winnerSlots.length > 0) {
    return gamePlayers.find((p) => p.slot === winnerSlots[0]) ?? null
  }
  if (!gameState) return null

  const remaining = gameState.players.filter(
    (pid) => !gameState.eliminated.includes(pid),
  )
  const winnerPlayerId = remaining[0] ?? null
  if (!winnerPlayerId) return null

  return gamePlayers.find((p) => String(p.slot) === winnerPlayerId) ?? null
}

// ─── 5. Modal visibility conditions ──────────────────────────────────────────

/**
 * Whether the TurnInCardsModal should be MOUNTED (not just open/closed).
 *
 * The player's hand is readable throughout an active game, including during
 * another player's turn. The modal itself disables turn-in interactions unless
 * the viewer is in an eligible placement phase.
 *
 * The modal's `visible` prop controls open/close; this controls whether the
 * modal is rendered at all (important for re-mount/reset semantics). A
 * retained, blurred route must never keep a native modal mounted behind the
 * focused route.
 */
export function shouldMountTurnInCardsModal(input: {
  gameStatus: string
  gameState: GameStateProjection | null | undefined
  myPlayerId: string | null
  isMyTurn: boolean
  isScreenFocused: boolean
}): boolean {
  const { gameStatus, gameState, myPlayerId, isScreenFocused } = input

  if (!isScreenFocused) return false
  if (gameStatus !== 'ACTIVE') return false
  if (!gameState) return false
  if (!myPlayerId) return false
  return true
}

/** Cards may only be selected and turned in during the viewer's placement step. */
export function canTurnInCardsNow(input: {
  gameStatus: string
  gameState: GameStateProjection | null | undefined
  isMyTurn: boolean
}): boolean {
  const { gameStatus, gameState, isMyTurn } = input
  return (
    gameStatus === 'ACTIVE' &&
    !!gameState &&
    isMyTurn &&
    (gameState.phase === 'placement' || gameState.phase === 'post-elim-placement')
  )
}

/**
 * Whether the delete-game confirm modal should be MOUNTABLE.
 *
 * Mirrors the render gate at line ~1817:
 *   `game.status === 'LOBBY' && isAdmin`
 *
 * Actual visibility is controlled by the `deleteConfirmOpen` state bit.
 */
export function shouldMountDeleteConfirmModal(input: {
  gameStatus: string
  isAdmin: boolean
}): boolean {
  return input.gameStatus === 'LOBBY' && input.isAdmin
}

/**
 * Whether the color-picker modal should be MOUNTABLE.
 *
 * Mirrors the render gate at line ~1856:
 *   `game.status === 'LOBBY' && myPlayer != null`
 *
 * Actual visibility is controlled by the `colorPickerOpen` state bit.
 */
export function shouldMountColorPickerModal(input: {
  gameStatus: string
  myPlayer: GamePlayerResponse | null | undefined
}): boolean {
  return input.gameStatus === 'LOBBY' && input.myPlayer != null
}

// ─── 6. Available slots derivation ───────────────────────────────────────────

/**
 * Computes the available (unoccupied) slots for the slot picker in Lobby State B.
 *
 * Mirrors lines ~690-693:
 *   `const occupiedSlots = new Set(game.players.map((p) => p.slot))`
 *   `const availableSlots = Array.from({ length: game.maxPlayers }, (_, i) => i + 1)
 *     .filter((slot) => !occupiedSlots.has(slot))`
 *
 * Pure — takes only the minimum fields needed.
 */
export function deriveAvailableSlots(input: {
  maxPlayers: number
  occupiedSlots: number[]
}): number[] {
  const occupied = new Set(input.occupiedSlots)
  return Array.from({ length: input.maxPlayers }, (_, i) => i + 1).filter(
    (slot) => !occupied.has(slot),
  )
}

// ─── 7. Lobby viewer-is-player flag ──────────────────────────────────────────

/**
 * True iff the viewing user is already a joined player in this game.
 *
 * Mirrors line ~565:
 *   `const viewerIsPlayer = user != null && myPlayer != null`
 *
 * Extracted as a named helper so the split can reference it consistently.
 */
export function deriveViewerIsPlayer(input: {
  userId: string | null | undefined
  myPlayer: GamePlayerResponse | null | undefined
}): boolean {
  return input.userId != null && input.myPlayer != null
}

/**
 * Whether to open the live game WebSocket for this viewer.
 *
 * ONLY game members (players) get a socket. The server rejects non-members
 * with WS close code 1008 ("Not a game member") — which the client otherwise
 * treats as an expired-token signal and answers with refresh-and-retry. For a
 * non-host viewing a pending lobby to join (not yet a player), that's a futile
 * loop: token refresh can never grant membership, so it just churns refresh
 * tokens and flashes the "Reconnecting…" banner. Gating on membership means
 * those viewers never open a socket — they fall back to the game query's
 * 15 s poll (refetchInterval when !wsConnected), which is plenty for a lobby.
 *
 * Also covers the kicked-mid-session case: once the player is removed from
 * `players`, this flips false and the socket tears down cleanly instead of
 * reconnect-looping against 1008.
 */
export function shouldConnectGameSocket(input: {
  gameId: string | null | undefined
  userId: string | null | undefined
  players: Array<{ userId: string }> | null | undefined
}): boolean {
  if (!input.gameId || !input.userId || !input.players) return false
  return input.players.some((p) => p.userId === input.userId)
}

// ─── 8. End-game modal visibility ────────────────────────────────────────────

/**
 * Whether the EndGameModal should be rendered.
 *
 * Mirrors line ~2035:
 *   `game.status === 'COMPLETE' && gameState && !endGameDismissed`
 */
export function shouldShowEndGameModal(input: {
  gameStatus: string
  gameState: GameStateProjection | null | undefined
  endGameDismissed: boolean
}): boolean {
  return input.gameStatus === 'COMPLETE' && input.gameState != null && !input.endGameDismissed
}

/**
 * Completed games with purged event history retain their snapshot state but
 * expose no event rows, so their activity sidebar should not be rendered.
 */
export function shouldShowCompletedActivity(
  gameState: { events?: unknown[] } | null | undefined,
): boolean {
  return (gameState?.events?.length ?? 0) > 0
}

// ─── 9. Reconnecting banner conditions ───────────────────────────────────────
// HTTP notice policy is shared with the game-screen hook.
export { connectionBannerMessage } from './connectionStatus'

// ─── 10. currentTurnPlayerId helper ──────────────────────────────────────────

/**
 * Returns the engine slot-id string of the current player, or null.
 *
 * Mirrors line ~579:
 *   `const currentTurnPlayerId = gameState?.players[gameState.turnIndex] ?? null`
 */
export function deriveCurrentTurnPlayerId(
  gameState: GameStateProjection | null | undefined,
): string | null {
  if (!gameState) return null
  return gameState.players[gameState.turnIndex] ?? null
}
